import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  LogOut, 
  Package, 
  Users, 
  ChefHat, 
  FileText, 
  Settings, 
  Tag, 
  Printer, 
  User, 
  Gift, 
  ChevronDown,
  BarChart,
  Shield,
  Menu,
  Database,
  DollarSign,
  Ticket,
  Boxes,
  RefreshCw,
  ShoppingCart,
  Wallet
} from 'lucide-react';
import axios from 'axios';

import { useAuth, API } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import MyRestaurantTab from '../components/admin/MyRestaurantTab';
import CategoriesTab from '../components/admin/CategoriesTab';
import PrintersTab from '../components/admin/PrintersTab';
import ProductsTab from '../components/admin/ProductsTab';
import IngredientsTab from '../components/admin/IngredientsTab';
import InventoryTab from '../components/admin/InventoryTab';
import PurchasesTab from '../components/admin/PurchasesTab';
import RecipesTab from '../components/admin/RecipesTab';
import TablesTab from '../components/admin/TablesTab';
import OrdersTab from '../components/admin/OrdersTab';
import ReservationsTab from '../components/admin/ReservationsTab';
import LoyaltyTab from '../components/admin/LoyaltyTab';
import AccountsReceivableTab from '../components/admin/AccountsReceivableTab';
import ReportsTab from '../components/admin/ReportsTab';
import TaxesTab from '../components/admin/TaxesTab';
import CouponsTab from '../components/admin/CouponsTab';
import CacheStatsTab from '../components/admin/CacheStatsTab';
import SyncConfigTab from '../components/admin/SyncConfigTab';
import MenuConfigTab from '../components/admin/MenuConfigTab';
import PermissionsTab from '../components/admin/PermissionsTab';
import UserManagementTab from '../components/admin/UserManagementTab';

const AdminDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const { canViewModule, loading: permissionsLoading } = usePermissions();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('myrestaurant');
  const [menuConfig, setMenuConfig] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Programa de Fidelización: si el toggle del tenant (loyalty_enabled) está
  // apagado, el ítem "Fidelidad" no se muestra en el menú del admin.
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(true);

  useEffect(() => {
    // Cargar configuración del menú desde la API
    const loadMenuConfig = async () => {
      // Esperar a que los permisos estén cargados
      if (permissionsLoading) return;

      try {
        const response = await axios.get(`${API}/menu-config`, {
          headers: getAuthHeaders()
        });
        
        if (response.data && response.data.config) {
          // El menú persistido en el servidor puede ser ANTERIOR a pestañas nuevas
          // del código (Sincronización lo es): se completan con sus valores por
          // defecto, si no la pestaña nueva jamás aparece en un tenant que ya
          // guardó su menú y habría que re-plantarlo a mano.
          const fullConfig = mergeMissingDefaults(response.data.config);
          const visibleConfig = fullConfig.filter(item => item.visible).sort((a, b) => a.order - b.order);
          const filteredConfig = filterMenuByPermissions(visibleConfig);
          setMenuConfig(filteredConfig);
          // También guardar en localStorage como respaldo
          localStorage.setItem('menuConfig', JSON.stringify(fullConfig));
        } else {
          // Si no hay configuración en la BD, usar la por defecto
          const defaultConfig = getDefaultConfig();
          const visibleConfig = defaultConfig.filter(item => item.visible).sort((a, b) => a.order - b.order);
          const filteredConfig = filterMenuByPermissions(visibleConfig);
          setMenuConfig(filteredConfig);
        }
      } catch (error) {
        console.error('Error al cargar menuConfig desde la API:', error);
        // Si falla la API, intentar cargar desde localStorage
        const savedConfig = localStorage.getItem('menuConfig');
        if (savedConfig) {
          try {
            const config = mergeMissingDefaults(JSON.parse(savedConfig));
            const visibleConfig = config.filter(item => item.visible).sort((a, b) => a.order - b.order);
            const filteredConfig = filterMenuByPermissions(visibleConfig);
            setMenuConfig(filteredConfig);
          } catch (parseError) {
            console.error('Error al parsear menuConfig desde localStorage:', parseError);
            // Si todo falla, usar configuración por defecto
            const defaultConfig = getDefaultConfig();
            const filteredConfig = filterMenuByPermissions(defaultConfig);
            setMenuConfig(filteredConfig);
          }
        } else {
          // Primera vez: no existe configuración guardada
          const defaultConfig = getDefaultConfig();
          const filteredConfig = filterMenuByPermissions(defaultConfig);
          setMenuConfig(filteredConfig);
        }
      }
    };

    // Cargar configuración inicial
    loadMenuConfig();

    // Escuchar cambios en la configuración del menú (desde MenuConfigTab)
    const handleMenuConfigChange = () => {
      loadMenuConfig();
    };

    window.addEventListener('menuConfigChanged', handleMenuConfigChange);

    // Limpiar listener al desmontar
    return () => {
      window.removeEventListener('menuConfigChanged', handleMenuConfigChange);
    };
  }, [permissionsLoading, canViewModule, loyaltyEnabled]); // Recargar cuando cambien los permisos o el flag de fidelidad

  // Leer el flag loyalty_enabled del tenant para ocultar el menú "Fidelidad"
  // cuando el Programa de Fidelización esté deshabilitado por el superadmin.
  useEffect(() => {
    const loadTenantFlags = async () => {
      try {
        const response = await axios.get(`${API}/tenants/my-tenant`, {
          headers: getAuthHeaders()
        });
        setLoyaltyEnabled(response.data?.loyalty_enabled !== false);
      } catch (error) {
        console.error('Error al cargar flags del tenant:', error);
        // Sin datos del tenant (p.ej. super_admin): se mantiene visible por defecto
        setLoyaltyEnabled(true);
      }
    };
    loadTenantFlags();
  }, []);

  // Función pura para obtener la configuración por defecto del menú
  // No modifica estado ni localStorage - solo retorna el array
  const getDefaultConfig = () => {
    return [
      { id: 'myrestaurant', name: 'Mi Restaurante', icon: 'Settings', visible: true, order: 1, module: 'config' },
      { id: 'categories', name: 'Categorías', icon: 'Tag', visible: true, order: 2, module: 'categories' },
      { id: 'printers', name: 'Impresoras', icon: 'Printer', visible: true, order: 3, module: 'printers' },
      { 
        id: 'products', 
        name: 'Productos', 
        icon: 'Package', 
        visible: true, 
        order: 4, 
        module: 'products',
        hasSubmenu: true,
        submenu: [
          { id: 'products-list', name: 'Productos', icon: 'Package', visible: true, parentId: 'products', module: 'products' },
          { id: 'ingredients', name: 'Ingredientes', icon: 'Package', visible: true, parentId: 'products', module: 'inventory' },
          { id: 'inventory', name: 'Inventario', icon: 'Boxes', visible: true, parentId: 'products', module: 'inventory' },
          { id: 'purchases', name: 'Compras', icon: 'ShoppingCart', visible: true, parentId: 'products', module: 'purchases' },
          { id: 'recipes', name: 'Recetas', icon: 'ChefHat', visible: true, parentId: 'products', module: 'products' }
        ]
      },
      { id: 'tables', name: 'Mesas', icon: 'Users', visible: true, order: 5, module: 'tables' },
      { id: 'orders', name: 'Órdenes', icon: 'FileText', visible: true, order: 6, module: 'orders' },
      { id: 'reservations', name: 'Reservas', icon: 'FileText', visible: true, order: 7, module: 'reservations' },
      { id: 'loyalty', name: 'Fidelidad', icon: 'Gift', visible: true, order: 8, module: 'customers' },
      { id: 'reports', name: 'Reportes', icon: 'Settings', visible: true, order: 9, module: 'reports' },
      { id: 'accounts-receivable', name: 'Cuentas por Cobrar', icon: 'Wallet', visible: true, order: 10, module: 'customers' },
      { id: 'coupons', name: 'Cupones', icon: 'Ticket', visible: true, order: 11, module: 'config' },
      { id: 'cache-stats', name: 'Caché Redis', icon: 'Database', visible: true, order: 12, module: 'config' },
      { id: 'permissions', name: 'Permisos', icon: 'Shield', visible: true, order: 14, module: 'config' },
      { id: 'sync', name: 'Sincronización', icon: 'RefreshCw', visible: true, order: 15, module: 'config' },
      { id: 'menuconfig', name: 'Config. Menú', icon: 'Settings', visible: true, order: 13, isSystem: true, module: 'config' },
      { id: 'users', name: 'Usuarios', icon: 'Users', visible: true, order: 16, module: 'users' }
    ];
  };

  // Pestañas que existen en el código pero faltan en un menú ya persistido:
  // se devuelven combinadas (las del servidor mandan, las nuevas se agregan al final).
  // También sincroniza los submenús: si un padre persistido no trae un subítem nuevo
  // (p.ej. Inventario), se inyecta desde getDefaultConfig() en su orden canónico.
  const mergeMissingDefaults = (config) => {
    const base = Array.isArray(config) ? config : [];
    const defaults = getDefaultConfig();
    const known = new Set(base.map((item) => item?.id));
    const missing = defaults.filter((item) => item?.id && !known.has(item.id));
    const merged = [...base, ...missing];
    const defaultsById = new Map(defaults.map((item) => [item?.id, item]));
    return merged.map((item) => {
      const def = defaultsById.get(item?.id);
      if (!def || !def.hasSubmenu || !Array.isArray(def.submenu)) return item;
      const currentSub = Array.isArray(item.submenu) ? item.submenu : [];
      const currentById = new Map(currentSub.map((s) => [s?.id, s]));
      const ordered = def.submenu.map((s) => currentById.get(s.id) || s);
      const defSubIds = new Set(def.submenu.map((s) => s?.id));
      const extra = currentSub.filter((s) => s?.id && !defSubIds.has(s.id));
      return { ...item, submenu: [...ordered, ...extra] };
    });
  };

  // Pestañas retiradas del menú. "Impuestos" ya no afecta a las órdenes: el
  // ITBIS es fijo (18%) y se activa/desactiva con el toggle "Cálculo de
  // Impuestos" del superadmin, así que su configuración se oculta del menú
  // (incluso para super_admin y aunque venga en una config ya guardada).
  const HIDDEN_MENU_IDS = ['taxes'];

  // Filtrar módulos según permisos del usuario
  const filterMenuByPermissions = (config) => {
    if (!config || config.length === 0) return [];
    
    return config.filter(item => {
      if (HIDDEN_MENU_IDS.includes(item.id)) return false;

      // Fidelidad: se oculta si el Programa de Fidelización está deshabilitado
      if (item.id === 'loyalty' && !loyaltyEnabled) return false;

      // Super admin ve todo
      if (user?.role === 'super_admin') return true;
      
      // Permisos: solo super_admin y admin
      if (item.id === 'permissions' && user?.role !== 'super_admin' && user?.role !== 'admin') {
        return false;
      }

      // Verificar si el usuario puede ver este módulo
      if (item.module && !canViewModule(item.module)) {
        return false;
      }

      // Si tiene submenú, filtrar los items del submenú también
      if (item.hasSubmenu && item.submenu) {
        item.submenu = item.submenu.filter(sub => {
          if (sub.module && !canViewModule(sub.module)) {
            return false;
          }
          return true;
        });
        
        // Si después del filtrado no quedan items en el submenú, ocultar el menú padre
        if (item.submenu.length === 0) {
          return false;
        }
      }

      return true;
    });
  };

  // Mapeo de iconos
  const iconMap = {
    Settings,
    Tag,
    Printer,
    Package,
    Users,
    FileText,
    Gift,
    ChefHat,
    BarChart,
    Shield,
    Database,
    DollarSign,
    Ticket,
    Boxes,
    RefreshCw,
    ShoppingCart,
    Wallet
  };

  const getIcon = (iconName) => {
    const Icon = iconMap[iconName] || Settings;
    return <Icon className="w-4 h-4 mr-2" />;
  };

  // Mapeo de tabs a componentes
  const componentMap = {
    'myrestaurant': MyRestaurantTab,
    'categories': CategoriesTab,
    'printers': PrintersTab,
    'products': ProductsTab,
    'products-list': ProductsTab,
    'ingredients': IngredientsTab,
    'inventory': InventoryTab,
    'purchases': PurchasesTab,
    'recipes': RecipesTab,
    'tables': TablesTab,
    'orders': OrdersTab,
    'reservations': ReservationsTab,
    'loyalty': LoyaltyTab,
    'accounts-receivable': AccountsReceivableTab,
    'reports': ReportsTab,
    'menuconfig': MenuConfigTab,
    'permissions': PermissionsTab,
    'taxes': TaxesTab,
    'coupons': CouponsTab,
    'cache-stats': CacheStatsTab,
    'sync': SyncConfigTab,
    'users': UserManagementTab,
  };

  // Función para obtener el componente correcto
  const getComponent = (id) => {
    return componentMap[id] || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-orange-100 sticky top-0 z-50 shadow-lg">
        <div className="max-w-full mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Mobile Menu Hamburger */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="lg:hidden p-2 border-orange-200">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[300px] sm:w-[350px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Menú Admin</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-2">
                    {menuConfig.filter(item => item.visible !== false).map((item) => {
                      if (item.hasSubmenu && item.submenu) {
                        return (
                          <div key={item.id} className="space-y-1">
                            <p className="text-xs font-semibold text-gray-500 uppercase px-3 py-2">{item.name}</p>
                            {item.submenu.filter(sub => sub.visible !== false).map((sub) => (
                              <Button
                                key={sub.id}
                                variant={activeTab === sub.id ? "default" : "ghost"}
                                onClick={() => {
                                  setActiveTab(sub.id);
                                  setMobileMenuOpen(false);
                                }}
                                className="w-full justify-start"
                              >
                                {getIcon(sub.icon)}
                                {sub.name}
                              </Button>
                            ))}
                          </div>
                        );
                      } else {
                        return (
                          <Button
                            key={item.id}
                            variant={activeTab === item.id ? "default" : "ghost"}
                            onClick={() => {
                              setActiveTab(item.id);
                              setMobileMenuOpen(false);
                            }}
                            className="w-full justify-start"
                          >
                            {getIcon(item.icon)}
                            {item.name}
                          </Button>
                        );
                      }
                    })}
                    <div className="pt-4 border-t mt-4 space-y-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate('/profile');
                          setMobileMenuOpen(false);
                        }}
                        className="w-full justify-start"
                      >
                        <User className="w-4 h-4 mr-2" />
                        Mi Perfil
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={logout}
                        className="w-full justify-start"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Cerrar Sesión
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform flex-shrink-0">
                <LayoutDashboard className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Panel Admin</h1>
                <p className="text-xs sm:text-sm text-gray-600 font-medium hidden sm:block">{user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Config Menu Button - Hidden on mobile */}
              <Button
                variant="outline"
                onClick={() => setActiveTab('menuconfig')}
                className="hidden sm:flex border-2 border-blue-300 hover:bg-blue-50 hover:border-blue-400 rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                title="Configurar Menú"
              >
                <Settings className="w-4 h-4 sm:mr-2 text-blue-600" />
                <span className="hidden md:inline font-semibold text-blue-600">Config. Menú</span>
              </Button>
              
              {/* User Menu - Hidden on mobile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="hidden lg:flex border-2 border-orange-300 hover:bg-orange-50 hover:border-orange-400 rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    <User className="w-4 h-4 sm:mr-2 text-orange-600" />
                    <span className="hidden sm:inline font-semibold text-orange-600">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 ml-1 text-orange-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 shadow-xl rounded-xl">
                  <DropdownMenuLabel className="text-base font-bold">Mi Cuenta</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="py-3 cursor-pointer">
                    <User className="w-4 h-4 mr-2" />
                    <span className="font-medium">Perfil</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="py-3 cursor-pointer text-red-600 hover:bg-red-50">
                    <LogOut className="w-4 h-4 mr-2" />
                    <span className="font-medium">Salir</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-full mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          {/* TabsList - Hidden on mobile (< lg), menu is in hamburger */}
          {/* h-auto anula el h-9 base de TabsList: con altura fija las pestañas
              que envuelven a la segunda fila quedan recortadas/tapadas por el
              panel y no se puede hacer clic en ellas (Sincronización, Cupones,
              Reportes... se volvían inalcanzables al crecer el menú). */}
          <TabsList className="hidden lg:flex w-full h-auto flex-wrap items-center justify-start gap-3 bg-white/90 backdrop-blur-xl p-3 rounded-2xl border-2 border-orange-100 shadow-xl">
            {menuConfig.map((item) => {
              if (item.hasSubmenu && item.submenu && item.submenu.length > 0) {
                // Menú con submenú - renderizar dropdown
                const submenuIds = item.submenu.map(sub => sub.id);
                const isActive = submenuIds.includes(activeTab) || item.id === activeTab;
                
                return (
                  <DropdownMenu key={item.id}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={`h-11 px-4 py-2 inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 ${
                          isActive
                            ? 'bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white shadow-orange-300'
                            : 'bg-white hover:bg-orange-50 text-gray-700 border-2 border-orange-200'
                        }`}
                      >
                        {getIcon(item.icon)}
                        {item.name}
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56 shadow-xl rounded-xl">
                      {item.submenu.filter(sub => sub.visible !== false).map((sub) => (
                        <DropdownMenuItem 
                          key={sub.id} 
                          onClick={() => setActiveTab(sub.id)}
                          className="py-3 cursor-pointer font-medium hover:bg-orange-50"
                        >
                          {getIcon(sub.icon)}
                          {sub.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              } else {
                // Menú simple - renderizar TabsTrigger
                return (
                  <TabsTrigger 
                    key={item.id} 
                    value={item.id} 
                    className="h-11 px-4 rounded-xl font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:via-red-500 data-[state=active]:to-pink-500 data-[state=active]:text-white data-[state=active]:shadow-orange-300 data-[state=inactive]:bg-white data-[state=inactive]:border-2 data-[state=inactive]:border-orange-200 data-[state=inactive]:hover:bg-orange-50"
                  >
                    {getIcon(item.icon)}
                    {item.name}
                  </TabsTrigger>
                );
              }
            })}
          </TabsList>

          {/* Renderizar contenido dinámicamente */}
          {/* MenuConfig siempre disponible */}
          <TabsContent key="menuconfig" value="menuconfig">
            <MenuConfigTab />
          </TabsContent>
          
          {menuConfig.map((item) => {
            // Saltar menuconfig ya que se renderiza explícitamente arriba
            if (item.id === 'menuconfig') {
              return null;
            }

            // Renderizar contenido del menú principal (solo si no tiene submenús)
            const Component = getComponent(item.id);
            if (Component && !item.hasSubmenu) {
              return (
                <TabsContent key={item.id} value={item.id}>
                  <Component />
                </TabsContent>
              );
            }
            
            // Renderizar contenido de los submenús
            if (item.hasSubmenu && item.submenu) {
              return item.submenu.map((sub) => {
                const SubComponent = getComponent(sub.id);
                if (SubComponent) {
                  return (
                    <TabsContent key={sub.id} value={sub.id}>
                      <SubComponent />
                    </TabsContent>
                  );
                }
                return null;
              });
            }
            
            return null;
          })}
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
